import { zodResolver } from "@hookform/resolvers/zod";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import { getECPs } from "../../services/ecp.service";
import { Customer, CustomerUpdateRequest } from "../../types/crm";
import { ECP } from "../../types/ecp";
import { stripPhonePrefix } from "../../utils/phone";

// Thai mobile: 9 digits after +66, starting with 6, 8, or 9
const thaiMobileRegex = /^[689]\d{8}$/;

const customerSchema = z.object({
  name: z.string().min(1, "First name is required").max(100, "Name too long"),
  surname: z
    .string()
    .min(1, "Last name is required")
    .max(100, "Surname too long"),
  email: z.string().email("Invalid email address").optional().or(z.literal("")),
  phone: z
    .string()
    .min(1, "Phone number is required")
    .transform((val) => val.replace(/\D/g, ""))
    .pipe(
      z
        .string()
        .regex(thaiMobileRegex, "Invalid Thai phone number (e.g. 812345678)"),
    ),
  ecp_code: z.string().min(1, "ECP code is required"),
  shop_name: z.string().optional(),
});

type FormValues = z.infer<typeof customerSchema>;

type PropsType = {
  customer?: Customer | null;
  onSubmit: (data: CustomerUpdateRequest) => Promise<void>;
  onCancel: () => void;
};

const labelSx = {
  fontSize: 16,
  lineHeight: 1.4,
  color: "#25272C",
};


const CustomerForm = ({ customer, onSubmit, onCancel }: PropsType) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [ecpOptions, setEcpOptions] = useState<ECP[]>([]);
  const [ecpLoading, setEcpLoading] = useState(false);
  const [selectedEcp, setSelectedEcp] = useState<ECP | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(customerSchema),
    defaultValues: {
      name: customer?.name || "",
      surname: customer?.surname || "",
      email: customer?.email || "",
      phone: stripPhonePrefix(customer?.phone),
      ecp_code: customer?.ecp_code || "",
      shop_name: customer?.shop_name || "",
    },
  });

  const searchECPs = useCallback((inputValue: string) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    if (!inputValue || inputValue.length < 2) {
      setEcpOptions([]);
      return;
    }
    setEcpLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const response = await getECPs({
          page_number: 1,
          page_size: 10,
          is_active: true,
          code: inputValue.toUpperCase(),
        });
        setEcpOptions(response?.ecps || []);
      } catch {
        setEcpOptions([]);
      } finally {
        setEcpLoading(false);
      }
    }, 500);
  }, []);

  useEffect(() => {
    if (customer?.ecp_code) {
      const fetchInitialEcp = async () => {
        try {
          const response = await getECPs({
            page_number: 1,
            page_size: 1,
            is_active: true,
            code: customer.ecp_code,
          });
          if (response?.ecps?.length) {
            setSelectedEcp(response.ecps[0]);
            setEcpOptions(response.ecps);
          }
        } catch {
          const syntheticEcp = {
            code: customer.ecp_code,
            name: customer.shop_name,
          } as ECP;
          setSelectedEcp(syntheticEcp);
          setEcpOptions([syntheticEcp]);
        }
      };
      fetchInitialEcp();
    }
  }, [customer?.ecp_code, customer?.shop_name]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleFormSubmit = async (data: FormValues) => {
    setIsSubmitting(true);
    try {
      await onSubmit({
        ...data,
        phone: `0${data.phone}`,
      } as CustomerUpdateRequest);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit(handleFormSubmit)}>
      <Stack spacing={3}>
        {/* ECP CODE - Searchable Autocomplete */}
        <Box>
          <Typography sx={labelSx}>
            ECP CODE
            <Typography component="span" sx={{ color: "#BE2217" }}>
              *
            </Typography>
          </Typography>
          <Box sx={{ mt: 1 }}>
            <Controller
              name="ecp_code"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <Autocomplete
                  options={ecpOptions}
                  value={selectedEcp}
                  getOptionLabel={(option) => option.code || ""}
                  isOptionEqualToValue={(option, value) =>
                    option.code === value.code
                  }
                  filterOptions={(x) => x}
                  loading={ecpLoading}
                  disabled={isSubmitting}
                  onInputChange={(_event, newInputValue, reason) => {
                    if (reason === "input") {
                      searchECPs(newInputValue);
                    }
                  }}
                  onChange={(_event, newValue) => {
                    setSelectedEcp(newValue);
                    field.onChange(newValue?.code || "");
                    setValue("shop_name", newValue?.name || "");
                  }}
                  renderOption={(props, option) => {
                    const { key, ...rest } = props;
                    return (
                      <li key={option.id || option.code} {...rest}>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {option.code}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.name}
                          </Typography>
                        </Box>
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={!!error}
                      helperText={error?.message}
                      placeholder="Search ECP code..."
                      slotProps={{
                        input: {
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {ecpLoading ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        },
                      }}
                    />
                  )}
                />
              )}
            />
          </Box>
        </Box>

        {/* Store Name + Line Name - Read Only */}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={3}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={labelSx}>Store Name</Typography>
            <Box sx={{ mt: 1 }}>
              <Controller
                name="shop_name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value || ""}
                    fullWidth
                    disabled
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        bgcolor: "#EDEEF1",
                        "& fieldset": { borderColor: "#D8DBDF" },
                      },
                    }}
                  />
                )}
              />
            </Box>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={labelSx}>Line Name</Typography>
            <Box sx={{ mt: 1 }}>
              <TextField
                value={customer?.line_name || ""}
                fullWidth
                disabled
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "#EDEEF1",
                    "& fieldset": { borderColor: "#D8DBDF" },
                  },
                }}
              />
            </Box>
          </Box>
        </Stack>

        {/* First Name + Last Name - Editable */}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={3}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={labelSx}>
              First Name
              <Typography component="span" sx={{ color: "#BE2217" }}>
                *
              </Typography>
            </Typography>
            <Box sx={{ mt: 1 }}>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    disabled={isSubmitting}
                  />
                )}
              />
            </Box>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={labelSx}>
              Last Name
              <Typography component="span" sx={{ color: "#BE2217" }}>
                *
              </Typography>
            </Typography>
            <Box sx={{ mt: 1 }}>
              <Controller
                name="surname"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    error={!!errors.surname}
                    helperText={errors.surname?.message}
                    disabled={isSubmitting}
                  />
                )}
              />
            </Box>
          </Box>
        </Stack>

        {/* Email + Phone Number - Editable */}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={3}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={labelSx}>
              Email
            </Typography>
            <Box sx={{ mt: 1 }}>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="email"
                    fullWidth
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    disabled={isSubmitting}
                  />
                )}
              />
            </Box>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={labelSx}>
              Phone Number
              <Typography component="span" sx={{ color: "#BE2217" }}>
                *
              </Typography>
            </Typography>
            <Box sx={{ mt: 1 }}>
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    placeholder="812345678"
                    error={!!errors.phone}
                    helperText={errors.phone?.message}
                    disabled={isSubmitting}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">+66</InputAdornment>
                        ),
                      },
                      htmlInput: {
                        maxLength: 9,
                        inputMode: "numeric",
                      },
                    }}
                  />
                )}
              />
            </Box>
          </Box>
        </Stack>

        {/* Action Buttons */}
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button
            variant="outlined"
            onClick={onCancel}
            disabled={isSubmitting}
            sx={{
              height: 40,
              borderRadius: "96px",
              borderColor: "#111",
              color: "#111",
              fontWeight: 700,
              fontSize: 16,
              textTransform: "none",
              px: 3,
            }}
          >
            Discard
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
            sx={{
              height: 40,
              borderRadius: "96px",
              bgcolor: "#111",
              color: "#fff",
              fontWeight: 700,
              fontSize: 16,
              textTransform: "none",
              px: 3,
              "&:hover": { bgcolor: "#333" },
            }}
          >
            Save
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default CustomerForm;
